import React, { useEffect, useRef, useState } from "react";
import { GET_IMAGE } from "../../helpers/images";
import defaultAvatar from "../../img/avatar2.png";
// eslint-disable-next-line import/no-extraneous-dependencies
import { Icon } from "@iconify/react";
import Favorite from "./Favorite";
import {
  REACTION_ICON_TYPE,
  REACTION_PROPERTY_TYPE,
} from "../../constants/reactions";
import CommentAction from "./CommentAction";
import moment from "moment";

const Comment = ({
  comment,
  jobId,
  currentUser,
  onToggleFavorite = () => {},
  onEditComment = () => {},
  onDeleteComment = () => {},
}) => {
  const [toggleFavorite, setToggleFavorite] = useState(false);
  const [currentReact, setCurrentReact] = useState(null);
  const { users, content, reacts, createdAt } = comment;
  const user = users[0];
  const isUserOwnComment = user._id === currentUser._id;
  const [actionVisible, setActionVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(content);
  const [isSaving, setIsSaving] = useState(false);
  const editInputRef = useRef(null);

  useEffect(() => {
    if (isEditing) editInputRef.current?.focus();
  }, [isEditing]);

  const handleStartEdit = () => {
    setEditValue(content);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditValue(content);
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === content) {
      setIsEditing(false);
      setEditValue(content);
      return;
    }
    setIsSaving(true);
    try {
      await onEditComment(comment._id, trimmed);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleFavorite = () => {
    const isActive = !toggleFavorite;
    setToggleFavorite(isActive);

    onToggleFavorite(
      comment._id,
      currentUser._id,
      isActive,
      currentReact,
      REACTION_ICON_TYPE.FAVORITE,
      REACTION_PROPERTY_TYPE.COMMENT,
    );
  };

  React.useEffect(() => {
    const userReactFavorite = reacts?.find(
      (react) => react.users[0]._id === currentUser._id,
    );
    setCurrentReact(userReactFavorite);
    setToggleFavorite(!!userReactFavorite);
  }, [reacts, currentUser]);
  
  const displayName = `${user.firstName.charAt(0).toUpperCase()}${user.firstName.slice(1)} ${user.lastName.charAt(0).toUpperCase()}${user.lastName.slice(1)}`;

  return (
    <div
      className="flex flex-row gap-2 items-start py-1"
      onMouseEnter={() => setActionVisible(true)}
      onMouseLeave={() => setActionVisible(false)}
    >
      <div className="flex-none">
        <img
          key={user?._id}
          className="w-8 h-8 rounded-full"
          src={user?.image ? GET_IMAGE(user.image) : defaultAvatar}
          alt=""
        />
      </div>
      <div className="relative bg-[#F4F5F8] rounded-2xl px-3 py-2 flex-1 min-w-0">
        <div className="flex items-center gap-2 leading-none mb-0.5">
          <span className="text-[13px] font-bold text-[#0B1320]">{displayName}</span>
          <span className="text-[11px] text-[#9AA1B0]" style={{ fontWeight: 500 }}>
            {moment(createdAt).fromNow()}
          </span>
        </div>
        {isEditing ? (
          <div>
            <textarea
              ref={editInputRef}
              className="w-full text-[13px] text-[#2A3142] bg-white rounded-lg border border-gray-300 px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              rows={2}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              disabled={isSaving}
            />
            <div className="flex items-center justify-end gap-2 mt-1">
              <button
                type="button"
                className="text-[11px] font-semibold text-[#9AA1B0] hover:text-[#2A3142]"
                onClick={handleCancelEdit}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="text-[11px] font-semibold text-blue-600 hover:text-blue-800 disabled:opacity-50"
                onClick={handleSaveEdit}
                disabled={isSaving || !editValue.trim()}
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="text-[13px] text-[#2A3142] flex-1 break-words leading-snug">
              {content}
            </div>
            {/* Same row as the toggle, not a row of its own below the text —
                anything below either overlaps a short comment's text/button
                (absolute) or adds a visible gap under it (in flow). Sitting
                beside the button costs no extra height at all. */}
            {!!reacts?.length && (
              <div className="flex-none">
                <Favorite reacts={reacts} />
              </div>
            )}
            <button
              type="button"
              className="font-medium rounded-full flex-none -mr-1"
              onClick={handleToggleFavorite}
              aria-label="Favorite comment"
            >
              <Icon
                icon={toggleFavorite ? "ant-design:heart-filled" : "ant-design:heart-outlined"}
                className={toggleFavorite ? "!text-[16px] text-pink-500" : "!text-[16px] text-[#9AA1B0]"}
              />
            </button>
          </div>
        )}
      </div>

      <div className="flex-none">
        {actionVisible && isUserOwnComment && !isEditing && (
          <CommentAction
            commentId={comment._id}
            jobId={jobId}
            onEdit={handleStartEdit}
            onDeleteComment={onDeleteComment}
          />
        )}
      </div>
    </div>
  );
};

export default Comment;
